import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiBell, FiUser, FiMenu, FiX, FiCheck, FiPackage, FiStar, FiMessageSquare, FiShield } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef(null);
  const isLoggedIn = localStorage.getItem('token');
  const userRole = localStorage.getItem('userRole');
  const isAdmin = userRole === 'admin';

  // Fetch notifications when logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchNotifications();
    }
  }, [isLoggedIn]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://extension.avalw.com/api/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const notifs = response.data.data || [];
      setNotifications(notifs.slice(0, 10)); // Show last 10
      setUnreadCount(notifs.filter(n => !n.isRead).length);
    } catch (error) {
      console.log('No notifications');
      setNotifications([]);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`https://extension.avalw.com/api/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch('https://extension.avalw.com/api/notifications/read-all', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'extension_approved':
      case 'extension_rejected':
        return <FiPackage className="text-gray-600" />;
      case 'new_review':
        return <FiStar className="text-yellow-500" />;
      case 'review_reply':
        return <FiMessageSquare className="text-blue-500" />;
      default:
        return <FiBell className="text-gray-600" />;
    }
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b-2 border-gray-200 shadow-sm">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center text-2xl">
              🧩
            </div>
            <span className="text-xl font-bold text-gray-900">
              AVALW
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Extensions</Link>
            <Link to="/developers" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Developers</Link>
            <Link to="/docs" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Documentation</Link>
            <Link to="/ai-assistant" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Build with AI</Link>
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-4">
            {isLoggedIn ? (
              <>
                {/* Notifications Dropdown */}
                <div className="relative" ref={notifRef}>
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    <FiBell className="text-xl" />
                    {unreadCount > 0 && (
                      <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  <AnimatePresence>
                    {showNotifications && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border-2 border-gray-200 overflow-hidden z-50"
                      >
                        {/* Header */}
                        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900">Notifications</h3>
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllAsRead}
                              className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1"
                            >
                              <FiCheck size={14} /> Mark all read
                            </button>
                          )}
                        </div>

                        {/* Notifications List */}
                        <div className="max-h-80 overflow-y-auto">
                          {notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center text-gray-500">
                              <FiBell className="mx-auto text-3xl mb-2 text-gray-300" />
                              <p>No notifications yet</p>
                            </div>
                          ) : (
                            notifications.map((notif) => (
                              <div
                                key={notif.id}
                                onClick={() => !notif.isRead && markAsRead(notif.id)}
                                className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                                  !notif.isRead ? 'bg-blue-50' : ''
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="mt-1">
                                    {getNotificationIcon(notif.type)}
                                  </div>
                                  <div className="flex-1">
                                    <p className={`text-sm ${!notif.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                      {notif.title}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">{notif.message}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                      {new Date(notif.createdAt).toLocaleDateString()}
                                    </p>
                                  </div>
                                  {!notif.isRead && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {isAdmin && (
                  <Link to="/admin" className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-all font-medium">
                    <FiShield />
                    <span>Admin</span>
                  </Link>
                )}
                <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all text-gray-900 font-medium">
                  <FiUser />
                  <span>Dashboard</span>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="px-6 py-2 text-gray-600 hover:text-gray-900 transition-colors font-medium">
                  Log In
                </Link>
                <Link to="/register" className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium">
                  Sign Up
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-gray-900"
          >
            {isMenuOpen ? <FiX className="text-2xl" /> : <FiMenu className="text-2xl" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden mt-4 py-4 border-t-2 border-gray-200"
          >
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Extensions</Link>
              <Link to="/developers" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Developers</Link>
              <Link to="/docs" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Documentation</Link>
              <Link to="/ai-assistant" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Build with AI</Link>
              {!isLoggedIn && (
                <>
                  <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors font-medium">Log In</Link>
                  <Link to="/register" className="px-6 py-2 bg-gray-900 text-white rounded-lg text-center hover:bg-gray-800 transition-all font-medium">
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
